import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Package, Plus, Pencil, Trash2, ChevronRight } from "lucide-react";

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", details: "" });

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm({ name: "", details: "" }); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setForm({ name: p.name, details: p.details }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Product name required");
    try {
      if (edit) await api.put(`/products/${edit.id}`, { ...edit, ...form });
      else await api.post("/products", form);
      toast.success(edit ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product removed");
    load();
  };

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <Package className="h-5 w-5 text-slate-900" />
          </div>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Products Library</h1>
            <p className="text-sm text-slate-500">Reuse item names & specs when creating a bid.</p>
          </div>
        </div>
        <Button onClick={openNew} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Product
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {products.map((p) => (
          <Card key={p.id} data-testid={`product-${p.id}`} onClick={() => navigate(`/products/${p.id}`)} className="p-5 rounded-xl border-border group cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all">
            <div className="flex items-start justify-between">
              <div className="font-head font-bold flex items-center gap-1.5">{p.name}<ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" /></div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(ev) => { ev.stopPropagation(); openEdit(p); }} data-testid={`edit-product-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={(ev) => { ev.stopPropagation(); remove(p.id); }} data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            {p.details && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{p.details}</p>}
            <div className="mt-3 text-[11px] text-slate-400">Open for Past Performance →</div>
          </Card>
        ))}
        {products.length === 0 && <div className="text-sm text-slate-500 col-span-full py-10 text-center">No products saved yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Product / Item Name (as in GeM catalogue)</Label>
              <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Specification / Details (optional)</Label>
              <Textarea data-testid="product-details-input" rows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1.5 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={submit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="save-product-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
